import { useEffect, useState } from 'react';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import CustomDataGrid from '../../../reusable-components/datagrid/CustomDataGrid';
import AddButton from '../../../reusable-components/basic-buttons/AddButton';
import EditIconButton from '../../../reusable-components/basic-buttons/EditIconButton';
import { RENTAL_FACILITY_ACCOUNTS } from '../../../reusable-components/datagrid/rentalColumns';
import { getFacilityAccounts, getVendorFacilities, getVendors, postDeleteRentalFacilityAccount } from '../../../api/rentals';
import { useScopeCheck } from '../../../reusable-components/scopes/useScopeCheck';
import { ConfirmDialog } from '../../../reusable-components/confirm-dialog';
import { useSnackbar } from '../../../reusable-components/snackbar';
import RentalFacilityAccountForm from './RentalFacilityAccountForm';

const RentalVendorAccountsTab = () => {
  const apiRef = useGridApiRef();
  const canEditRentalVendors = useScopeCheck(['Rental-Vendor-Edit'], true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [row, setRow] = useState(null);
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const deleteAccount = async (id) => {
    try {
      const response = await postDeleteRentalFacilityAccount(id);
      if (!response?.success) {
        enqueueSnackbar(response?.message || 'Unable to delete rental vendor account.', { variant: 'error' });
        return;
      }
      enqueueSnackbar('Rental vendor account deleted successfully.', { variant: 'success' });
      await fetchData();
    } catch {
      enqueueSnackbar('Unable to delete rental vendor account.', { variant: 'error' });
    }
  };

  const actionButtons = (params) => canEditRentalVendors && 
    <>
      <EditIconButton onClick={() => { setRow(params.row); setOpenForm(true); }} />
      <ConfirmDialog
        icon="delete-outline"
        color="error"
        content={`Are you sure you want to delete account ${params.row.accountNumber} for ${params.row.facility}?`}
        actionButton="Delete"
        action={() => deleteAccount(params.row.facilityVendorId)}
      />
    </>;

  const actions = {
    field: 'actions',
    headerName: 'Actions',
    width: 120,
    renderCell: (params) => actionButtons(params),
    id: 'actions',
    className: 'actions',
    sortable: false,
  };

  const fetchData = async () => {
    try {
      setDataLoaded(false);
      const response = await getFacilityAccounts();
      setRows(response);
      setDataLoaded(true);
    } catch (error) {
      console.error('Error fetching vendor data:', error);
      setDataLoaded(true);
    }
  };

  const fetchFormData = async () => {
    try {
      const [facilityData, vendorResponse] = await Promise.all([getVendorFacilities(), getVendors()]);
      setFacilities(facilityData);
      setVendors(vendorResponse.data);
    } catch (error) {
      console.error('Error fetching rental vendor form data:', error);
    }
  };

  useEffect(() => {
    fetchData();
    if (canEditRentalVendors) fetchFormData();
  }, []);

  return (
    <>
      <CustomDataGrid
        apiRef={apiRef}
        gridId="rental-vendor-accounts"
        boxSX={{ width: '100%', height: 'calc(100vh - 260px)' }}
        data={rows}
        getRowId={(row) => row.facilityVendorId}
        gridColumns={canEditRentalVendors ? [...RENTAL_FACILITY_ACCOUNTS, actions] : RENTAL_FACILITY_ACCOUNTS}
        CustomLeftToolbar={canEditRentalVendors ? () => <AddButton onClick={() => { setRow(null); setOpenForm(true); }}>Add Account</AddButton> : undefined}
        pinnedColumns={canEditRentalVendors ? { right: ['actions'] } : undefined}
        isLoading={!dataLoaded}
        sort={[{ field: 'name', sort: 'asc' }]}
      />
      <RentalFacilityAccountForm
        facilities={facilities}
        open={openForm}
        row={row}
        vendors={vendors}
        onClose={() => { setRow(null); setOpenForm(false); }}
        onSaved={fetchData}
      />
    </>
  );
};

export default RentalVendorAccountsTab;
